import React, { useEffect, useState } from 'react';

/**
 * Registration-time plan selection (Entitlement Sprint 2, step 8 — spec E6).
 * Shared selector offered during unit creation / condo join flows.
 * Default is always 'free' — no silent paid enrollment.
 */

export type PlanChoice = 'free' | 'home_plus' | 'home_ultra' | 'home_unlimited';

export interface PlanResult {
  status: 'free' | 'trialing' | 'checkout_required' | 'covered' | 'checkout_unavailable' | 'skipped' | 'paid';
  plan?: string;
  checkoutUrl?: string | null;
  note?: string;
  code?: string;
}

const OPTIONS: { id: PlanChoice; title: string; description: string }[] = [
  {
    id: 'free',
    title: 'Gratis',
    description: 'Funciones básicas para esta unidad.',
  },
  {
    id: 'home_plus',
    title: 'Home+',
    description: 'Desbloquea todos los módulos del hogar para esta unidad.',
  },
  {
    id: 'home_ultra',
    title: 'HomeUltra',
    description: 'Home+ en hasta 9 unidades de tu propiedad (las 9 más antiguas).',
  },
  {
    id: 'home_unlimited',
    title: 'HomeUnlimited',
    description: 'Home+ en TODAS tus unidades, sin límite.',
  },
];

// FIX 2026-09-18 (Jaime): prices are no longer hardcoded (stale $9.99 HomeUltra) —
// they come from plan_definitions via /api/billing/plans; skeleton until loaded.
export function PlanSelector({ value, onChange, disabled }: {
  value: PlanChoice;
  onChange: (choice: PlanChoice) => void;
  disabled?: boolean;
}) {
  const [planPrices, setPlanPrices] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/billing/plans').then(r => r.ok ? r.json() : null).then(x => {
      const plans = x?.data?.plans ?? x?.plans;
      if (!cancelled && Array.isArray(plans)) {
        setPlanPrices(Object.fromEntries(plans.map((pl: any) => [pl.id, Number(pl.price_usd ?? (Number(pl.price_usd_cents) || 0) / 100)])));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const renderPrice = (id: PlanChoice) => {
    if (id === 'free') return '$0';
    if (!planPrices) return <span className="inline-block h-4 w-16 rounded bg-slate-700 animate-pulse align-middle" aria-label="Cargando precio"></span>;
    const usd = planPrices[id];
    return usd != null ? `$${usd.toFixed(2)}/mes` : '';
  };
  return (
    <fieldset className="sm:col-span-2" disabled={disabled}>
      <legend className="text-sm font-semibold text-slate-300 mb-2">Plan para esta unidad</legend>
      <div className="grid grid-cols-1 gap-2">
        {OPTIONS.map((option) => {
          const selected = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.id)}
              className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                selected
                  ? 'border-[var(--v-accent,#36CCD8)] bg-[var(--v-accent,#36CCD8)]/10'
                  : 'border-slate-600 bg-slate-800 hover:border-slate-500'
              }`}
            >
              <span className="flex items-center justify-between">
                <span className="font-semibold text-white">{option.title}</span>
                <span className="text-sm text-slate-300">{renderPrice(option.id)}</span>
              </span>
              <span className="mt-1 block text-xs text-slate-400">{option.description}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Si tu condominio tiene un plan pagado, tu unidad ya está cubierta y no se realizará ningún cargo.
      </p>
    </fieldset>
  );
}

/**
 * Handle the `data.plan` descriptor returned by POST /api/units and /api/units/join.
 * Redirects to hosted checkout when payment is required; otherwise returns a
 * user-facing note (Spanish) or null when nothing needs to be surfaced.
 */
export function handlePlanResult(plan: PlanResult | undefined | null): string | null {
  if (!plan || !plan.status) return null;
  if (plan.status === 'checkout_required' && plan.checkoutUrl) {
    window.location.href = plan.checkoutUrl;
    return 'Redirigiendo al pago seguro…';
  }
  switch (plan.status) {
    case 'trialing':
      return '¡Prueba gratuita activada para tu plan!';
    case 'covered':
      return plan.note || 'Esta unidad ya está cubierta por un plan activo; no se realizó ningún cargo.';
    case 'checkout_unavailable':
      return plan.note || 'La unidad se registró en plan gratuito: el pago no está disponible por ahora. Puedes suscribirte después desde Facturación.';
    case 'skipped':
      return plan.note || 'La unidad quedó en plan gratuito.';
    default:
      return null; // free / paid — nothing to surface
  }
}
